/**
 * CombatScene — Full combat scene with PixiJS grid rendering
 * and HTML/CSS DOM panels for UI.
 *
 * PixiJS canvas: grid tiles, combatant shapes, HP bars, highlights, animations.
 * HTML/CSS DOM: turn order bar, action buttons, combat log, victory/defeat overlay.
 *
 * @module CombatScene
 */

import { Container, Graphics, Text } from 'pixi.js';
import debugLog from '../debug/DebugLog.js';
import sceneManager from '../core/SceneManager.js';
import eventBus from '../core/EventBus.js';
import focusManager from '../input/FocusManager.js';
import combatSystem, { skillSystem, tileEffectsSystem, companionSystem, combatAI } from '../systems/CombatSystem.js';
import inventorySystem from '../systems/InventorySystem.js';

// ── Constants ────────────────────────────────────────

/** Tile colors */
const TILE_COLOR_PLAYER_SIDE = 0x1a2a3a;
const TILE_COLOR_ENEMY_SIDE = 0x2a1a1a;
const TILE_COLOR_BORDER = 0x333355;
const TILE_HIGHLIGHT_MOVE = 0x2244aa;
const TILE_HIGHLIGHT_ATTACK = 0xaa2222;
const TILE_HIGHLIGHT_SELECTED = 0xccaa22;

/** Combatant colors */
const PLAYER_COLORS = [0x4466aa, 0x44aa44, 0xaa8844, 0x8844aa];
const COMPANION_COLOR = 0x44aaaa;
const ENEMY_COLOR = 0xcc3333;
const ENEMY_ELITE_COLOR = 0xcc9933;
const ENEMY_BOSS_COLOR = 0x991111;

/** Animation durations (ms) */
const ATTACK_FLASH_MS = 200;
const DEATH_FADE_MS = 500;
const ENEMY_AI_DELAY_MS = 600;

export default class CombatScene {
  constructor() {
    this.container = new Container();

    // PixiJS objects
    this._gridContainer = null;
    this._combatantContainer = null;
    this._highlightContainer = null;

    // Grid rendering params
    this._tileSize = 64;
    this._gridOffsetX = 0;
    this._gridOffsetY = 0;

    // Viewport
    this._width = 800;
    this._height = 600;

    // HTML DOM elements
    this._hudEl = null;
    this._turnBarEl = null;
    this._infoBarEl = null;
    this._logEl = null;
    this._actionsEl = null;
    this._overlayEl = null;
    this._targetPromptEl = null;

    // Scene data
    this._returnScene = 'WorldTravel';
    this._returnData = {};

    // Skill submenu element
    this._skillMenuEl = null;
    this._selectedSkillId = null;

    // Tile effects PixiJS container
    this._tileEffectContainer = null;

    // Interaction state
    this._selectedAction = null; // 'attack' | 'strongAttack' | 'move' | 'skill' | null
    this._highlightedTiles = [];

    // Combatant PixiJS sprites map: combatant.id -> { shape, hpBar, hpFill, nameText }
    this._combatantSprites = new Map();

    // Event handler references for cleanup
    this._eventHandlers = {};

    // Animation queue
    this._animations = [];

    // Tile cursor for keyboard/gamepad navigation
    this._tileCursorX = 0;
    this._tileCursorY = 0;
    this._tileCursorActive = false;
    this._tileCursorCallback = null;
    this._tileCursorValidTiles = [];

    // Input handler references for cleanup
    this._tileNavHandler = null;
    this._tileConfirmHandler = null;
    this._tileCancelHandler = null;
    this._tileTouchHandler = null;

    // Transparent DOM overlay for capturing tile clicks/touches
    // (avoids PixiJS canvas event interception)
    this._tileOverlayEl = null;
    this._tileDebugGridEl = null;
  }

  async init() {
    debugLog.info('CombatScene: init');
  }

  /**
   * Enter the combat scene.
   * @param {object} data - { party, enemies: [{id, level}...], returnScene, returnData }
   */
  async enter(data) {
    debugLog.event('CombatScene: enter');

    // Get viewport
    const canvas = document.getElementById('game-canvas');
    if (canvas) {
      this._width = canvas.clientWidth || 800;
      this._height = canvas.clientHeight || 600;
    }

    // Store return info
    this._returnScene = data?.returnScene || 'WorldTravel';
    this._returnData = data?.returnData || {};

    // Reset combat system
    combatSystem.reset();

    // Start combat
    const party = data?.party;
    const enemyDefs = data?.enemies || [{ id: 'wolf', level: 1 }];

    if (!party || !party.members || party.members.length === 0) {
      debugLog.warn('CombatScene: No party provided, using debug party');
      // Create a fallback so combat can still load
      const result = combatSystem.startCombat(
        { members: [{ name: 'Hero', hp: 100, maxHp: 100, mana: 50, maxMana: 50, attributes: { str: 12, agi: 12, int: 10, vit: 12, wis: 10, cha: 10 }, equipment: {} }] },
        enemyDefs
      );
      if (!result) {
        debugLog.error('CombatScene: Failed to start combat');
        sceneManager.transition(this._returnScene, this._returnData);
        return;
      }
    } else {
      const result = combatSystem.startCombat(party, enemyDefs);
      if (!result) {
        debugLog.error('CombatScene: Failed to start combat');
        sceneManager.transition(this._returnScene, this._returnData);
        return;
      }
    }

    // Build PixiJS grid
    this._calculateGridLayout();
    this._buildGrid();
    this._buildCombatants();
    this._updateHighlights();

    // Build HTML/CSS UI
    this._buildHUD();
    this._updateTurnBar();
    this._updateInfoBar();
    this._updateActionButtons();
    this._syncLog();

    // Subscribe to events
    this._subscribeEvents();

    // Register debug commands
    this._registerDebugCommands();

    // Expose for testing
    if (window.__debug) {
      window.__debug.combatSystem = combatSystem;
      window.__debug.combatScene = this;
      window.__debug.skillSystem = skillSystem;
      window.__debug.tileEffects = tileEffectsSystem;
      window.__debug.combatAI = combatAI;
    }

    // If first turn is enemy, kick off AI
    const current = combatSystem.getCurrentTurn();
    if (current && current.type === 'enemy') {
      this._scheduleEnemyAI();
    }
  }

  exit() {
    debugLog.event('CombatScene: exit');

    this._unsubscribeEvents();
    this._removeTileClickHandler();

    // Remove all DOM elements
    if (this._hudEl && this._hudEl.parentNode) {
      this._hudEl.parentNode.removeChild(this._hudEl);
    }
    this._hudEl = null;

    if (this._overlayEl && this._overlayEl.parentNode) {
      this._overlayEl.parentNode.removeChild(this._overlayEl);
    }
    this._overlayEl = null;

    if (this._targetPromptEl && this._targetPromptEl.parentNode) {
      this._targetPromptEl.parentNode.removeChild(this._targetPromptEl);
    }
    this._targetPromptEl = null;

    // Clean up skill menu
    this._closeSkillMenu();

    // Clear state
    this._combatantSprites.clear();
    this._animations = [];
    this._selectedAction = null;
    this._selectedSkillId = null;

    focusManager.clear();
  }

  update(dt) {
    // Process animations
    this._processAnimations(dt);
  }

  resize(w, h) {
    this._width = w;
    this._height = h;
    this._calculateGridLayout();
    this._rebuildGrid();
  }

  // ── Grid Layout Calculation ─────────────────────────

  _calculateGridLayout() {
    const rows = combatSystem.rows;
    const cols = combatSystem.cols;
    if (rows === 0 || cols === 0) return;

    // Reserve space for UI: top 80px for turn bar, bottom 70px for actions
    const availW = this._width - 40;
    const availH = this._height - 160;

    const tileW = Math.floor(availW / cols);
    const tileH = Math.floor(availH / rows);
    this._tileSize = Math.min(tileW, tileH, 80);

    const gridW = cols * this._tileSize;
    const gridH = rows * this._tileSize;

    this._gridOffsetX = Math.floor((this._width - gridW) / 2);
    this._gridOffsetY = Math.floor((this._height - gridH) / 2) + 20;
  }

  // ── PixiJS Grid ─────────────────────────────────────

  _buildGrid() {
    this._gridContainer = new Container();
    this._highlightContainer = new Container();
    this._tileEffectContainer = new Container();
    this._combatantContainer = new Container();

    const rows = combatSystem.rows;
    const cols = combatSystem.cols;
    const ts = this._tileSize;

    const grid = new Graphics();

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = this._gridOffsetX + c * ts;
        const y = this._gridOffsetY + r * ts;
        const isPlayerSide = c < cols / 2;
        const fillColor = isPlayerSide ? TILE_COLOR_PLAYER_SIDE : TILE_COLOR_ENEMY_SIDE;

        grid.rect(x, y, ts, ts);
        grid.fill(fillColor);
        grid.rect(x, y, ts, ts);
        grid.stroke({ color: TILE_COLOR_BORDER, width: 1 });
      }
    }

    this._gridContainer.addChild(grid);

    this.container.addChild(this._gridContainer);
    this.container.addChild(this._tileEffectContainer);
    this.container.addChild(this._highlightContainer);
    this.container.addChild(this._combatantContainer);
  }

  _rebuildGrid() {
    // Clear and rebuild
    if (this._gridContainer) {
      this.container.removeChild(this._gridContainer);
      this._gridContainer.destroy({ children: true });
    }
    if (this._tileEffectContainer) {
      this.container.removeChild(this._tileEffectContainer);
      this._tileEffectContainer.destroy({ children: true });
    }
    if (this._highlightContainer) {
      this.container.removeChild(this._highlightContainer);
      this._highlightContainer.destroy({ children: true });
    }
    if (this._combatantContainer) {
      this.container.removeChild(this._combatantContainer);
      this._combatantContainer.destroy({ children: true });
    }
    this._combatantSprites.clear();
    this._buildGrid();
    this._buildCombatants();
    this._updateHighlights();
    this._updateTileEffects();
  }

  // ── Combatant Sprites ───────────────────────────────

  _buildCombatants() {
    const ts = this._tileSize;

    const allCombatants = [...combatSystem.players, ...combatSystem.companionCombatants, ...combatSystem.enemies];

    for (let i = 0; i < allCombatants.length; i++) {
      const c = allCombatants[i];
      const group = new Container();

      // Body shape
      const shape = new Graphics();
      const isPlayer = c.type === 'player';
      const isCompanion = c.type === 'companion';
      const playerIdx = isPlayer ? combatSystem.players.indexOf(c) : -1;
      let color;
      if (isCompanion) {
        color = COMPANION_COLOR;
      } else if (isPlayer) {
        color = PLAYER_COLORS[playerIdx % PLAYER_COLORS.length];
      } else if (c.variant === 'boss' || c.behavior === 'boss') {
        color = ENEMY_BOSS_COLOR;
      } else if (c.variant === 'elite') {
        color = ENEMY_ELITE_COLOR;
      } else {
        color = ENEMY_COLOR;
      }

      const w = ts * 0.6;
      const h = ts * 0.7;
      shape.rect(-w / 2, -h, w, h);
      shape.fill(color);

      // Head
      const headR = ts * 0.12;
      shape.circle(0, -h - headR + 2, headR);
      shape.fill(isCompanion ? 0x88dddd : (isPlayer ? 0xddbbaa : 0x998877));

      group.addChild(shape);

      // Elite golden border
      if (!isPlayer && !isCompanion && c.variant === 'elite') {
        const eliteBorder = new Graphics();
        const bw = w + 4;
        const bh = h + 4;
        eliteBorder.rect(-bw / 2, -h - 2, bw, bh);
        eliteBorder.stroke({ color: 0xffcc00, width: 2 });
        group.addChild(eliteBorder);
      }

      // Boss red glow
      if (!isPlayer && !isCompanion && (c.variant === 'boss' || c.behavior === 'boss')) {
        const bossGlow = new Graphics();
        const gw = w + 8;
        const gh = h + 8;
        bossGlow.rect(-gw / 2, -h - 4, gw, gh);
        bossGlow.stroke({ color: 0xff2200, width: 3, alpha: 0.6 });
        group.addChild(bossGlow);
      }

      // HP bar background
      const hpBarWidth = ts * 0.7;
      const hpBarHeight = 4;
      const hpBar = new Graphics();
      hpBar.rect(-hpBarWidth / 2, -h - headR * 2 - 8, hpBarWidth, hpBarHeight);
      hpBar.fill(0x111111);
      hpBar.rect(-hpBarWidth / 2, -h - headR * 2 - 8, hpBarWidth, hpBarHeight);
      hpBar.stroke({ color: 0x444444, width: 1 });
      group.addChild(hpBar);

      // HP bar fill
      const hpFill = new Graphics();
      const hpRatio = Math.max(0, c.hp / c.maxHp);
      const hpColor = hpRatio > 0.5 ? 0x44cc66 : (hpRatio > 0.25 ? 0xffaa22 : 0xff4444);
      hpFill.rect(-hpBarWidth / 2, -h - headR * 2 - 8, hpBarWidth * hpRatio, hpBarHeight);
      hpFill.fill(hpColor);
      group.addChild(hpFill);

      // Position
      const px = this._gridOffsetX + c.gridX * ts + ts / 2;
      const py = this._gridOffsetY + c.gridY * ts + ts;
      group.x = px;
      group.y = py;

      if (!c.alive) {
        group.alpha = 0.2;
      }

      this._combatantContainer.addChild(group);
      this._combatantSprites.set(c.id, {
        group,
        shape,
        hpBar,
        hpFill,
        hpBarWidth,
        bodyH: h,
        headR,
      });
    }
  }

  _updateCombatantSprite(combatant) {
    const sprite = this._combatantSprites.get(combatant.id);
    if (!sprite) return;

    const ts = this._tileSize;

    // Update position
    sprite.group.x = this._gridOffsetX + combatant.gridX * ts + ts / 2;
    sprite.group.y = this._gridOffsetY + combatant.gridY * ts + ts;

    // Update HP fill
    if (sprite.hpFill) {
      sprite.hpFill.clear();
      const hpRatio = Math.max(0, combatant.hp / combatant.maxHp);
      const hpColor = hpRatio > 0.5 ? 0x44cc66 : (hpRatio > 0.25 ? 0xffaa22 : 0xff4444);
      sprite.hpFill.rect(
        -sprite.hpBarWidth / 2,
        -sprite.bodyH - sprite.headR * 2 - 8,
        sprite.hpBarWidth * hpRatio,
        4
      );
      sprite.hpFill.fill(hpColor);
    }

    // Death
    if (!combatant.alive) {
      sprite.group.alpha = 0.2;
    }
  }

  _updateAllCombatantSprites() {
    const all = [...combatSystem.players, ...combatSystem.companionCombatants, ...combatSystem.enemies];
    for (const c of all) {
      this._updateCombatantSprite(c);
    }
  }

  // ── Tile Highlights ─────────────────────────────────

  _updateHighlights() {
    if (!this._highlightContainer) return;

    // Clear old
    this._highlightContainer.removeChildren();
    const hg = new Graphics();

    const current = combatSystem.getCurrentTurn();
    if (!current) {
      this._highlightContainer.addChild(hg);
      return;
    }

    const ts = this._tileSize;

    // Highlight selected unit tile
    if (current.alive) {
      const sx = this._gridOffsetX + current.gridX * ts;
      const sy = this._gridOffsetY + current.gridY * ts;
      hg.rect(sx + 1, sy + 1, ts - 2, ts - 2);
      hg.fill({ color: TILE_HIGHLIGHT_SELECTED, alpha: 0.3 });
    }

    // Show action-specific highlights
    if (this._selectedAction === 'move' && current.type === 'player') {
      const moveTiles = combatSystem.getMovementRange(current);
      for (const tile of moveTiles) {
        const tx = this._gridOffsetX + tile.x * ts;
        const ty = this._gridOffsetY + tile.y * ts;
        hg.rect(tx + 1, ty + 1, ts - 2, ts - 2);
        hg.fill({ color: TILE_HIGHLIGHT_MOVE, alpha: 0.35 });
      }
    } else if ((this._selectedAction === 'attack' || this._selectedAction === 'strongAttack' || this._selectedAction === 'quickAttack') && current.type === 'player') {
      const targets = combatSystem.getAttackRange(current);
      for (const t of targets) {
        const tx = this._gridOffsetX + t.x * ts;
        const ty = this._gridOffsetY + t.y * ts;
        hg.rect(tx + 1, ty + 1, ts - 2, ts - 2);
        hg.fill({ color: TILE_HIGHLIGHT_ATTACK, alpha: 0.35 });
      }
    } else if (this._selectedAction === 'skill' && this._selectedSkillId && current.type === 'player') {
      const targets = skillSystem.getSkillTargets(current, this._selectedSkillId);
      for (const t of targets) {
        const tx = this._gridOffsetX + t.x * ts;
        const ty = this._gridOffsetY + t.y * ts;
        hg.rect(tx + 1, ty + 1, ts - 2, ts - 2);
        hg.fill({ color: 0x8844cc, alpha: 0.35 });
      }
    }

    // Tile cursor indicator
    if (this._tileCursorActive) {
      const cx = this._gridOffsetX + this._tileCursorX * ts;
      const cy = this._gridOffsetY + this._tileCursorY * ts;
      hg.rect(cx + 1, cy + 1, ts - 2, ts - 2);
      hg.stroke({ color: 0xffffff, width: 2, alpha: 0.9 });
    }

    this._highlightContainer.addChild(hg);
  }

  // ── Animations ──────────────────────────────────────

  _processAnimations(dt) {
    const dtMs = dt * 1000;
    for (let i = this._animations.length - 1; i >= 0; i--) {
      const anim = this._animations[i];
      anim.elapsed += dtMs;

      if (anim.type === 'flash') {
        const sprite = this._combatantSprites.get(anim.targetId);
        if (sprite) {
          if (anim.elapsed < anim.duration) {
            // Flash white
            const t = anim.elapsed / anim.duration;
            sprite.group.alpha = 0.4 + Math.abs(Math.sin(t * Math.PI * 3)) * 0.6;
          } else {
            sprite.group.alpha = anim.targetAlive ? 1 : 0.2;
            this._animations.splice(i, 1);
          }
        } else {
          this._animations.splice(i, 1);
        }
      } else if (anim.type === 'shake') {
        const sprite = this._combatantSprites.get(anim.targetId);
        if (sprite) {
          if (anim.elapsed < anim.duration) {
            const intensity = 3 * (1 - anim.elapsed / anim.duration);
            sprite.group.x = anim.origX + (Math.random() - 0.5) * intensity * 2;
          } else {
            sprite.group.x = anim.origX;
            this._animations.splice(i, 1);
          }
        } else {
          this._animations.splice(i, 1);
        }
      } else if (anim.type === 'fadeOut') {
        const sprite = this._combatantSprites.get(anim.targetId);
        if (sprite) {
          if (anim.elapsed < anim.duration) {
            sprite.group.alpha = 1 - (anim.elapsed / anim.duration) * 0.8;
          } else {
            sprite.group.alpha = 0.2;
            this._animations.splice(i, 1);
          }
        } else {
          this._animations.splice(i, 1);
        }
      }
    }
  }

  _animateAttack(targetId, alive) {
    const sprite = this._combatantSprites.get(targetId);
    if (!sprite) return;

    this._animations.push({
      type: 'flash',
      targetId,
      targetAlive: alive,
      duration: ATTACK_FLASH_MS,
      elapsed: 0,
    });
    this._animations.push({
      type: 'shake',
      targetId,
      origX: sprite.group.x,
      duration: ATTACK_FLASH_MS,
      elapsed: 0,
    });
  }

  _animateDeath(targetId) {
    this._animations.push({
      type: 'fadeOut',
      targetId,
      duration: DEATH_FADE_MS,
      elapsed: 0,
    });
  }

  // ── HTML/CSS HUD ────────────────────────────────────

  _buildHUD() {
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this._hudEl = document.createElement('div');
    this._hudEl.id = 'combat-hud';
    this._hudEl.className = 'combat-hud';
    this._hudEl.setAttribute('data-scene', 'Combat');

    // Turn order bar
    this._turnBarEl = document.createElement('div');
    this._turnBarEl.className = 'combat-turn-bar';
    this._turnBarEl.id = 'combat-turn-bar';
    this._hudEl.appendChild(this._turnBarEl);

    // Info bar
    this._infoBarEl = document.createElement('div');
    this._infoBarEl.className = 'combat-info-bar';
    this._infoBarEl.id = 'combat-info-bar';
    this._hudEl.appendChild(this._infoBarEl);

    // Spacer — empty flex area between top UI and bottom actions.
    // pointer-events: none so clicks pass through to the canvas grid.
    const spacer = document.createElement('div');
    spacer.className = 'combat-hud-spacer';
    this._hudEl.appendChild(spacer);

    // Combat log (absolute positioned, floats over the spacer area)
    this._logEl = document.createElement('div');
    this._logEl.className = 'combat-log';
    this._logEl.id = 'combat-log';
    this._hudEl.appendChild(this._logEl);

    // Action buttons (flex child at bottom, not absolute)
    this._actionsEl = document.createElement('div');
    this._actionsEl.className = 'combat-actions';
    this._actionsEl.id = 'combat-actions';
    this._hudEl.appendChild(this._actionsEl);

    uiRoot.appendChild(this._hudEl);
  }

  // ── Turn Order Bar ──────────────────────────────────

  _updateTurnBar() {
    if (!this._turnBarEl) return;
    this._turnBarEl.innerHTML = '';

    const label = document.createElement('span');
    label.className = 'combat-turn-label';
    label.textContent = 'Turn:';
    this._turnBarEl.appendChild(label);

    const currentIdx = combatSystem.turnIndex;

    for (let i = 0; i < combatSystem.turnOrder.length; i++) {
      const c = combatSystem.turnOrder[i];
      const entry = document.createElement('div');
      let cls = 'combat-turn-entry';
      if (i === currentIdx) cls += ' combat-turn-entry--active';
      if (!c.alive) cls += ' combat-turn-entry--dead';
      if (c.type === 'companion') {
        cls += ' combat-turn-entry--companion';
      } else {
        cls += c.type === 'player' ? ' combat-turn-entry--player' : ' combat-turn-entry--enemy';
      }
      entry.className = cls;

      const nameSpan = document.createElement('span');
      nameSpan.className = 'combat-turn-name';
      nameSpan.textContent = c.name;
      entry.appendChild(nameSpan);

      const hpSpan = document.createElement('span');
      hpSpan.className = 'combat-turn-hp';
      hpSpan.textContent = c.alive ? `${c.hp}/${c.maxHp}` : 'Dead';
      entry.appendChild(hpSpan);

      // Auto-AI toggle for player characters
      if (c.type === 'player' && c.alive) {
        const autoBtn = document.createElement('button');
        const isAuto = combatSystem.isAutoAI(c.id);
        autoBtn.className = `combat-auto-ai-btn${isAuto ? ' combat-auto-ai-btn--active' : ''}`;
        autoBtn.id = `combat-auto-ai-${c.id}`;
        autoBtn.setAttribute('data-focusable', 'true');
        autoBtn.setAttribute('data-character-id', c.id);
        autoBtn.setAttribute('aria-label', `Toggle auto-AI for ${c.name}`);
        autoBtn.textContent = isAuto ? 'AUTO' : 'MAN';
        autoBtn.title = isAuto ? 'Auto-AI enabled (click to disable)' : 'Manual control (click to enable auto-AI)';
        autoBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          combatSystem.toggleAutoAI(c.id);
          this._updateTurnBar();
          // If toggling on during this character's turn, trigger auto-AI
          const current = combatSystem.getCurrentTurn();
          if (current && current.id === c.id && combatSystem.isAutoAI(c.id)) {
            combatSystem.state = 'autoTurn';
            this._scheduleAutoAI();
          }
        });
        entry.appendChild(autoBtn);
      }

      this._turnBarEl.appendChild(entry);
    }

    // Add companion entries at the end of the turn bar
    for (const comp of combatSystem.companionCombatants) {
      const entry = document.createElement('div');
      let cls = 'combat-turn-entry combat-turn-entry--companion';
      if (!comp.alive) cls += ' combat-turn-entry--dead';
      entry.className = cls;

      const nameSpan = document.createElement('span');
      nameSpan.className = 'combat-turn-name';
      nameSpan.textContent = comp.name;
      entry.appendChild(nameSpan);

      const hpSpan = document.createElement('span');
      hpSpan.className = 'combat-turn-hp';
      hpSpan.textContent = comp.alive ? `${comp.hp}/${comp.maxHp}` : 'Dead';
      entry.appendChild(hpSpan);

      this._turnBarEl.appendChild(entry);
    }
  }

  // ── Info Bar ────────────────────────────────────────

  _updateInfoBar() {
    if (!this._infoBarEl) return;

    const current = combatSystem.getCurrentTurn();
    if (!current) {
      this._infoBarEl.innerHTML = '';
      return;
    }

    this._infoBarEl.innerHTML = `
      <span class="combat-info-name">${current.name}'s Turn</span>
      <span class="combat-info-ap">AP: ${current.ap}/${current.maxAp}</span>
      <span class="combat-info-mana">Mana: ${current.mana}/${current.maxMana}</span>
      <span class="combat-info-round">Round ${combatSystem.turnCount}</span>
    `;
  }

  // ── Action Buttons ──────────────────────────────────

  _updateActionButtons() {
    if (!this._actionsEl) return;
    this._actionsEl.innerHTML = '';

    const current = combatSystem.getCurrentTurn();
    if (!current || current.type !== 'player' || combatSystem.state === 'victory' || combatSystem.state === 'defeat' || combatSystem.state === 'autoTurn') {
      // During enemy turn or end state, show no actions
      // But pop any focus group for actions
      return;
    }

    const actions = [
      { id: 'attack',       label: 'Attack',        cost: 2, cls: 'combat-action-btn--attack' },
      { id: 'strongAttack', label: 'Strong Attack',  cost: 3, cls: 'combat-action-btn--strong' },
      { id: 'skills',       label: 'Skills',         cost: 0, cls: 'combat-action-btn--skills' },
      { id: 'move',         label: 'Move',           cost: 1, cls: 'combat-action-btn--move' },
      { id: 'defend',       label: 'Defend',         cost: 2, cls: 'combat-action-btn--defend' },
      { id: 'useItem',      label: 'Use Item',       cost: 1, cls: 'combat-action-btn--item' },
      { id: 'endTurn',      label: 'End Turn',       cost: 0, cls: 'combat-action-btn--end' },
    ];

    for (const action of actions) {
      const btn = document.createElement('button');
      btn.className = `combat-action-btn ${action.cls}`;
      btn.id = `combat-btn-${action.id}`;
      btn.setAttribute('data-focusable', 'true');
      btn.setAttribute('data-action', action.id);

      const disabled = action.cost > 0 && current.ap < action.cost;
      if (disabled) {
        btn.disabled = true;
        btn.classList.add('combat-action-btn--disabled');
      }

      btn.innerHTML = `${action.label}${action.cost > 0 ? `<span class="combat-btn-cost">(${action.cost})</span>` : ''}`;

      btn.addEventListener('click', () => this._onActionClick(action.id));
      this._actionsEl.appendChild(btn);
    }

    // Push focus group for actions
    // Clear existing focus groups first to avoid stacking
    focusManager.clear();
    focusManager.pushGroup('combat-actions', this._actionsEl);
  }

  _onActionClick(actionId) {
    const current = combatSystem.getCurrentTurn();
    if (!current || current.type !== 'player') return;
    if (combatSystem.state === 'victory' || combatSystem.state === 'defeat') return;

    switch (actionId) {
      case 'attack':
        this._beginTargetSelection('attack');
        break;
      case 'strongAttack':
        this._beginTargetSelection('strongAttack');
        break;
      case 'skills':
        this._openSkillMenu();
        break;
      case 'move':
        this._beginMoveSelection();
        break;
      case 'defend':
        this._doDefend();
        break;
      case 'useItem':
        this._doUseItem();
        break;
      case 'endTurn':
        this._doEndTurn();
        break;
    }
  }

  // ── Target / Move Selection ─────────────────────────

  _beginTargetSelection(attackType) {
    const current = combatSystem.getCurrentTurn();
    if (!current) return;

    const targets = combatSystem.getAttackRange(current);
    if (targets.length === 0) {
      this._addLogEntry('No targets in range!');
      return;
    }

    // Clear focus manager so gamepad input goes to tile selection, not UI buttons
    focusManager.clear();

    this._selectedAction = attackType;
    this._updateHighlights();

    // If only one target, auto-select it
    if (targets.length === 1) {
      this._executeAttack(targets[0].combatant, attackType);
      return;
    }

    // Show target prompt
    this._showTargetPrompt('Select a target (click, arrow keys + Enter, or D-pad + A)');

    // Set up click handler on canvas for tile selection
    this._setupTileClickHandler((x, y) => {
      const occupant = combatSystem.getUnitAt(x, y);
      if (occupant && occupant.alive && occupant.type !== current.type) {
        this._hideTargetPrompt();
        this._removeTileClickHandler();
        this._executeAttack(occupant, attackType);
      }
    }, targets);
  }

  _beginMoveSelection() {
    const current = combatSystem.getCurrentTurn();
    if (!current) return;

    const moveTiles = combatSystem.getMovementRange(current);
    if (moveTiles.length === 0) {
      this._addLogEntry('Cannot move (no AP or no valid tiles)!');
      return;
    }

    // Clear focus manager so gamepad input goes to tile selection, not UI buttons
    focusManager.clear();

    this._selectedAction = 'move';
    this._updateHighlights();

    this._showTargetPrompt('Select a tile to move to (click, arrow keys + Enter, or D-pad + A)');

    this._setupTileClickHandler((x, y) => {
      // Check if this tile is in movement range
      const isValidMove = moveTiles.some(t => t.x === x && t.y === y);
      if (isValidMove) {
        this._hideTargetPrompt();
        this._removeTileClickHandler();
        this._executeMove(x, y);
      }
    }, moveTiles);
  }

  _executeAttack(target, attackType) {
    const current = combatSystem.getCurrentTurn();
    if (!current) return;

    const type = attackType === 'strongAttack' ? 'strong' : (attackType === 'quickAttack' ? 'quick' : 'basic');
    const result = combatSystem.attack(current, target, type);

    if (result.hit) {
      this._animateAttack(target.id, target.alive);
      if (!target.alive) {
        this._animateDeath(target.id);
      }
    }

    this._selectedAction = null;
    this._afterPlayerAction();
  }

  _executeMove(x, y) {
    const current = combatSystem.getCurrentTurn();
    if (!current) return;

    // Find path and walk it step by step (moveUnit only accepts adjacent moves)
    const path = combatSystem.findPath(current, x, y);
    if (path.length === 0) {
      debugLog.log(`[Combat] No path found to (${x}, ${y})`);
      this._addLogEntry('Cannot reach that tile!');
      this._selectedAction = null;
      this._afterPlayerAction();
      return;
    }

    let moved = false;
    for (const step of path) {
      const result = combatSystem.moveUnit(current, step.x, step.y);
      if (!result.success) {
        debugLog.log(`[Combat] Move step to (${step.x}, ${step.y}) failed: ${result.message}`);
        break;
      }
      moved = true;
    }

    if (moved) {
      this._updateCombatantSprite(current);
    }
    this._selectedAction = null;
    this._afterPlayerAction();
  }

  _doDefend() {
    const current = combatSystem.getCurrentTurn();
    if (!current) return;
    if (current.ap < 2) {
      this._addLogEntry('Not enough AP to defend!');
      return;
    }

    current.ap -= 2;
    current.statusEffects.push({
      id: 'defending',
      name: 'Defending',
      duration: 1,
      effects: [],
    });

    combatSystem._addLog(`${current.name} takes a defensive stance.`);
    eventBus.emit('combat:defend', { combatant: current });

    this._afterPlayerAction();
  }

  _doUseItem() {
    const current = combatSystem.getCurrentTurn();
    if (!current) return;

    // Get consumable items from inventory
    const consumables = inventorySystem.getItems().filter(i => i.consumable);
    if (consumables.length === 0) {
      this._addLogEntry('No usable items!');
      return;
    }

    // Show item picker menu
    this._showItemPicker(current, consumables);
  }

  _showItemPicker(unit, consumables) {
    // Clear focus from action buttons
    focusManager.clear();

    // Create item picker overlay
    this._itemPickerEl = document.createElement('div');
    this._itemPickerEl.className = 'travel-dialog';
    this._itemPickerEl.style.zIndex = 'var(--z-modal)';

    const title = document.createElement('div');
    title.className = 'travel-dialog-title';
    title.textContent = 'Use Item';
    this._itemPickerEl.appendChild(title);

    const btnContainer = document.createElement('div');
    btnContainer.className = 'travel-dialog-buttons';

    for (const item of consumables) {
      const btn = document.createElement('button');
      btn.className = 'travel-dialog-btn';
      btn.setAttribute('data-focusable', 'true');

      const nameSpan = document.createElement('span');
      nameSpan.className = 'travel-dialog-btn-name';
      nameSpan.textContent = item.name;
      btn.appendChild(nameSpan);

      if (item.effect) {
        const descSpan = document.createElement('span');
        descSpan.className = 'travel-dialog-btn-level';
        if (item.effect.type === 'heal') descSpan.textContent = `+${item.effect.amount} HP`;
        else if (item.effect.type === 'damage') descSpan.textContent = `${item.effect.amount} dmg`;
        else if (item.effect.type === 'cureStatus') descSpan.textContent = 'Cure';
        btn.appendChild(descSpan);
      }

      btn.addEventListener('click', () => {
        this._hideItemPicker();
        combatSystem.useItem(unit, item);
        inventorySystem.removeItem(item.instanceId);
        this._afterPlayerAction();
      });

      btnContainer.appendChild(btn);
    }

    // Cancel button
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'travel-dialog-btn travel-dialog-btn-cancel';
    cancelBtn.setAttribute('data-focusable', 'true');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => {
      this._hideItemPicker();
      this._updateActionButtons();
    });
    btnContainer.appendChild(cancelBtn);

    this._itemPickerEl.appendChild(btnContainer);
    this._hudEl.appendChild(this._itemPickerEl);

    focusManager.pushGroup('combat-items', btnContainer);
  }

  _hideItemPicker() {
    if (this._itemPickerEl) {
      this._itemPickerEl.remove();
      this._itemPickerEl = null;
    }
    focusManager.clear();
    if (this._actionsEl) {
      focusManager.pushGroup('combat-actions', this._actionsEl);
    }
  }

  _doEndTurn() {
    this._selectedAction = null;
    this._hideTargetPrompt();
    this._removeTileClickHandler();

    // Run companion AI before ending the player's turn
    if (this._pendingCompanionAction) {
      const { companion } = this._pendingCompanionAction;
      this._pendingCompanionAction = null;

      if (companion.alive) {
        const actions = companionSystem.runCompanionAI(companion, combatSystem);

        // Animate companion attacks
        for (const action of actions) {
          if (action.type === 'attack' && action.result?.hit && action.target) {
            this._animateAttack(action.target.id, action.target.alive);
            if (!action.target.alive) {
              this._animateDeath(action.target.id);
            }
          }
        }

        this._updateAllCombatantSprites();
        this._updateTurnBar();

        // Check if combat ended from companion actions
        if (combatSystem.state === 'victory' || combatSystem.state === 'defeat') {
          return;
        }
      }
    }

    combatSystem.endTurn();
  }

  _afterPlayerAction() {
    // Update UI
    this._updateAllCombatantSprites();
    this._updateHighlights();
    this._updateTileEffects();
    this._updateTurnBar();
    this._updateInfoBar();
    this._updateStatusIcons();

    // Check if combat ended
    if (combatSystem.state === 'victory' || combatSystem.state === 'defeat') {
      return;
    }

    // Check if player still has AP and it's still their turn
    const current = combatSystem.getCurrentTurn();
    if (!current || current.type !== 'player' || current.ap <= 0) {
      // Auto end turn if no AP - run companion AI first
      if (current && current.type === 'player' && current.ap <= 0) {
        this._doEndTurn();
      }
      return;
    }

    // Refresh action buttons for updated AP
    this._updateActionButtons();
  }

  // ── Target Prompt ───────────────────────────────────

  _showTargetPrompt(text) {
    this._hideTargetPrompt();

    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this._targetPromptEl = document.createElement('div');
    this._targetPromptEl.className = 'combat-target-prompt';
    this._targetPromptEl.id = 'combat-target-prompt';
    this._targetPromptEl.textContent = text;

    uiRoot.appendChild(this._targetPromptEl);
  }

  _hideTargetPrompt() {
    if (this._targetPromptEl && this._targetPromptEl.parentNode) {
      this._targetPromptEl.parentNode.removeChild(this._targetPromptEl);
    }
    this._targetPromptEl = null;
  }

  // ── Tile Selection (mouse, keyboard, gamepad, touch) ──

  _setupTileClickHandler(callback, validTiles) {
    this._removeTileClickHandler();

    this._tileCursorCallback = callback;
    this._tileCursorValidTiles = validTiles || [];

    // Initialize cursor at first valid tile or current unit position
    if (this._tileCursorValidTiles.length > 0) {
      this._tileCursorX = this._tileCursorValidTiles[0].x;
      this._tileCursorY = this._tileCursorValidTiles[0].y;
      this._tileCursorActive = true;
    } else {
      const current = combatSystem.getCurrentTurn();
      if (current) {
        this._tileCursorX = current.gridX;
        this._tileCursorY = current.gridY;
      }
      this._tileCursorActive = true;
    }

    // ── Transparent pointer overlay ─────────────────────────────────────
    // Sits above the canvas (z-index: --z-modal) and captures ALL pointer
    // events reliably. This avoids PixiJS EventSystem intercepting
    // pointerdown on the canvas and suppressing synthetic click events.
    this._tileOverlayEl = document.createElement('div');
    this._tileOverlayEl.id = 'combat-tile-overlay';
    Object.assign(this._tileOverlayEl.style, {
      position: 'fixed',
      inset: '0',
      zIndex: 'var(--z-modal)',
      background: 'transparent',
      cursor: 'crosshair',
      touchAction: 'none',
    });

    // Pointer handler — handles mouse, touch, and stylus uniformly
    this._tileClickHandler = (e) => {
      e.preventDefault();
      // Only react to primary button (left mouse / first touch)
      if (e.button !== undefined && e.button !== 0) return;

      // clientX/Y are viewport coords; overlay is fixed at 0,0 so no offset needed
      const mx = e.clientX;
      const my = e.clientY;

      const col = Math.floor((mx - this._gridOffsetX) / this._tileSize);
      const row = Math.floor((my - this._gridOffsetY) / this._tileSize);

      debugLog.log(`[TileInput] pointerdown at (${Math.round(mx)}, ${Math.round(my)}) → tile (${col}, ${row}) | offset:(${this._gridOffsetX},${this._gridOffsetY}) tileSize:${this._tileSize}`);

      if (col >= 0 && col < combatSystem.cols && row >= 0 && row < combatSystem.rows) {
        const isValid = this._tileCursorValidTiles.length === 0 ||
          this._tileCursorValidTiles.some(t => t.x === col && t.y === row);
        debugLog.log(`[TileInput] tile (${col},${row}) inBounds=true valid=${isValid}`);
        callback(col, row);
      } else {
        debugLog.log(`[TileInput] tile (${col},${row}) out of bounds (grid ${combatSystem.cols}×${combatSystem.rows})`);
      }
    };

    this._tileOverlayEl.addEventListener('pointerdown', this._tileClickHandler);

    // Debug grid: draw tile boundaries over the overlay so clicks are visually verifiable
    this._tileDebugGridEl = this._buildDebugTileGrid();
    if (this._tileDebugGridEl) {
      this._tileOverlayEl.appendChild(this._tileDebugGridEl);
    }

    document.body.appendChild(this._tileOverlayEl);
    debugLog.log(`[TileInput] Overlay created — grid ${combatSystem.cols}×${combatSystem.rows}, tileSize=${this._tileSize}, offset=(${this._gridOffsetX},${this._gridOffsetY})`);

    // ── Keyboard navigation ──────────────────────────────────────────────
    this._tileNavHandler = (e) => {
      let dx = 0, dy = 0;
      switch (e.key) {
        case 'ArrowUp': case 'w': case 'W': dy = -1; break;
        case 'ArrowDown': case 's': case 'S': dy = 1; break;
        case 'ArrowLeft': case 'a': case 'A': dx = -1; break;
        case 'ArrowRight': case 'd': case 'D': dx = 1; break;
        default: return;
      }
      e.preventDefault();
      e.stopPropagation();
      debugLog.log(`[TileInput] key '${e.key}' → cursor (${this._tileCursorX + dx}, ${this._tileCursorY + dy})`);
      this._moveTileCursor(dx, dy);
    };

    this._tileConfirmHandler = (e) => {
      if (e.key === 'Enter' || e.key === 'e' || e.key === 'E' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        debugLog.log(`[TileInput] key confirm → tile (${this._tileCursorX}, ${this._tileCursorY}) active:${this._tileCursorActive}`);
        if (this._tileCursorActive) {
          callback(this._tileCursorX, this._tileCursorY);
        }
      }
    };

    this._tileCancelHandler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._cancelTileSelection();
      }
    };

    document.addEventListener('keydown', this._tileNavHandler, true);
    document.addEventListener('keydown', this._tileConfirmHandler, true);
    document.addEventListener('keydown', this._tileCancelHandler, true);

    // ── Gamepad polling ──────────────────────────────────────────────────
    // FIX: Seed _gamepadAWasPressed with the CURRENT A-button state so that
    // if the player used A to press the "Move" button (still held), the first
    // poll won't immediately confirm the tile selection.
    this._gamepadAWasPressed = false;
    this._gamepadBWasPressed = false;
    const gpads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const gp of gpads) {
      if (gp && gp.connected) {
        this._gamepadAWasPressed = gp.buttons[0]?.pressed || false;
        this._gamepadBWasPressed = gp.buttons[1]?.pressed || false;
        break;
      }
    }

    this._gamepadPollInterval = setInterval(() => this._pollGamepadForTiles(callback), 100);

    this._updateHighlights();
  }

  /**
   * Build a lightweight SVG debug grid that shows tile boundaries and
   * coordinates over the tile-selection overlay. Makes it easy to verify
   * that pointer clicks land on the correct tiles.
   * @returns {SVGElement|null}
   */
  _buildDebugTileGrid() {
    const cols = combatSystem.cols;
    const rows = combatSystem.rows;
    const ts = this._tileSize;
    const ox = this._gridOffsetX;
    const oy = this._gridOffsetY;
    if (!cols || !rows || !ts) return null;

    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', String(window.innerWidth));
    svg.setAttribute('height', String(window.innerHeight));
    Object.assign(svg.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      pointerEvents: 'none',
      opacity: '0.35',
    });

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = ox + c * ts;
        const y = oy + r * ts;

        const isValid = this._tileCursorValidTiles.length === 0 ||
          this._tileCursorValidTiles.some(t => t.x === c && t.y === r);

        // Tile border rect
        const rect = document.createElementNS(svgNS, 'rect');
        rect.setAttribute('x', String(x + 1));
        rect.setAttribute('y', String(y + 1));
        rect.setAttribute('width', String(ts - 2));
        rect.setAttribute('height', String(ts - 2));
        rect.setAttribute('fill', isValid ? 'rgba(100,180,255,0.08)' : 'none');
        rect.setAttribute('stroke', isValid ? '#5af' : '#446');
        rect.setAttribute('stroke-width', '1');
        svg.appendChild(rect);

        // Coordinate label (top-left corner of tile)
        const text = document.createElementNS(svgNS, 'text');
        text.setAttribute('x', String(x + 3));
        text.setAttribute('y', String(y + 11));
        text.setAttribute('font-size', '9');
        text.setAttribute('fill', '#9be');
        text.setAttribute('font-family', 'monospace');
        text.textContent = `${c},${r}`;
        svg.appendChild(text);
      }
    }

    return svg;
  }

  _moveTileCursor(dx, dy) {
    if (!this._tileCursorActive) return;

    const newX = this._tileCursorX + dx;
    const newY = this._tileCursorY + dy;

    // Clamp to grid bounds
    if (newX < 0 || newX >= combatSystem.cols || newY < 0 || newY >= combatSystem.rows) return;

    this._tileCursorX = newX;
    this._tileCursorY = newY;
    this._updateHighlights();
  }

  _pollGamepadForTiles(callback) {
    const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const gp of gamepads) {
      if (!gp || !gp.connected) continue;

      // D-pad navigation
      const up = gp.buttons[12]?.pressed;
      const down = gp.buttons[13]?.pressed;
      const left = gp.buttons[14]?.pressed;
      const right = gp.buttons[15]?.pressed;

      // Left stick navigation (with deadzone)
      const lx = gp.axes[0] || 0;
      const ly = gp.axes[1] || 0;
      const deadzone = 0.5;

      let dx = 0, dy = 0;
      if (left || lx < -deadzone) dx = -1;
      else if (right || lx > deadzone) dx = 1;
      if (up || ly < -deadzone) dy = -1;
      else if (down || ly > deadzone) dy = 1;

      if (dx !== 0 || dy !== 0) {
        this._moveTileCursor(dx, dy);
      }

      // A button (confirm) — only on press edge (was not pressed last poll)
      const aPressed = gp.buttons[0]?.pressed;
      if (aPressed && !this._gamepadAWasPressed && this._tileCursorActive) {
        this._gamepadAWasPressed = true;
        debugLog.log(`[Input] Gamepad A confirm → tile (${this._tileCursorX}, ${this._tileCursorY})`);
        callback(this._tileCursorX, this._tileCursorY);
        return;
      }
      this._gamepadAWasPressed = aPressed;

      // B button (cancel) — only on press edge
      const bPressed = gp.buttons[1]?.pressed;
      if (bPressed && !this._gamepadBWasPressed) {
        this._gamepadBWasPressed = true;
        this._cancelTileSelection();
        return;
      }
      this._gamepadBWasPressed = bPressed;
      break;
    }
  }

  _cancelTileSelection() {
    this._hideTargetPrompt();
    this._removeTileClickHandler();
    this._selectedAction = null;
    this._selectedSkillId = null;
    this._updateHighlights();
    this._updateActionButtons();
    // Restore focus to action buttons
    if (this._actionsEl) {
      focusManager.clear();
      focusManager.pushGroup('combat-actions', this._actionsEl);
    }
  }

  _removeTileClickHandler() {
    // Remove the transparent pointer-capture overlay
    if (this._tileOverlayEl) {
      if (this._tileClickHandler) {
        this._tileOverlayEl.removeEventListener('pointerdown', this._tileClickHandler);
      }
      if (this._tileOverlayEl.parentNode) {
        this._tileOverlayEl.parentNode.removeChild(this._tileOverlayEl);
      }
      this._tileOverlayEl = null;
      this._tileDebugGridEl = null;
    }
    this._tileClickHandler = null;

    if (this._tileNavHandler) {
      document.removeEventListener('keydown', this._tileNavHandler, true);
      this._tileNavHandler = null;
    }
    if (this._tileConfirmHandler) {
      document.removeEventListener('keydown', this._tileConfirmHandler, true);
      this._tileConfirmHandler = null;
    }
    if (this._tileCancelHandler) {
      document.removeEventListener('keydown', this._tileCancelHandler, true);
      this._tileCancelHandler = null;
    }
    if (this._gamepadPollInterval) {
      clearInterval(this._gamepadPollInterval);
      this._gamepadPollInterval = null;
    }
    this._tileCursorActive = false;
    this._tileCursorCallback = null;
    this._tileCursorValidTiles = [];
  }

  // ── Combat Log ──────────────────────────────────────

  _syncLog() {
    if (!this._logEl) return;
    this._logEl.innerHTML = '';

    const entries = combatSystem.getLog();
    for (const msg of entries) {
      this._addLogEntry(msg, false);
    }

    this._logEl.scrollTop = this._logEl.scrollHeight;
  }

  _addLogEntry(msg, scroll = true) {
    if (!this._logEl) return;

    const entry = document.createElement('div');
    entry.className = 'combat-log-entry';

    // Color-code based on content
    if (msg.includes('damage') || msg.includes('CRIT') || msg.includes('hits') || msg.includes('grazes')) {
      entry.classList.add('combat-log-entry--damage');
    } else if (msg.includes('restoring') || msg.includes('regenerate')) {
      entry.classList.add('combat-log-entry--heal');
    } else if (msg.includes('turn') || msg.includes('---')) {
      entry.classList.add('combat-log-entry--turn');
    } else {
      entry.classList.add('combat-log-entry--info');
    }

    entry.textContent = msg;
    this._logEl.appendChild(entry);

    if (scroll) {
      this._logEl.scrollTop = this._logEl.scrollHeight;
    }
  }

  // ── Enemy AI ────────────────────────────────────────

  _scheduleEnemyAI() {
    const current = combatSystem.getCurrentTurn();
    if (!current || current.type !== 'enemy') return;

    // Disable player actions during enemy turn
    this._updateActionButtons();

    setTimeout(() => {
      if (combatSystem.state !== 'active' && combatSystem.state !== 'enemyTurn') return;

      const actions = combatSystem.runEnemyAI(current);

      // Animate attacks
      for (const action of actions) {
        if (action.type === 'attack' && action.result?.hit && action.target) {
          this._animateAttack(action.target.id, action.target.alive);
          if (!action.target.alive) {
            this._animateDeath(action.target.id);
          }
        }
      }

      this._updateAllCombatantSprites();
      this._updateHighlights();
      this._updateTurnBar();
      this._updateInfoBar();

      // Check if combat ended
      if (combatSystem.state === 'victory' || combatSystem.state === 'defeat') {
        return;
      }

      // End enemy turn
      combatSystem.endTurn();
    }, ENEMY_AI_DELAY_MS);
  }

  // ── Auto-AI Scheduling ──────────────────────────────

  _scheduleAutoAI() {
    const current = combatSystem.getCurrentTurn();
    if (!current || current.type !== 'player') return;
    if (!combatSystem.isAutoAI(current.id)) return;

    // Disable player actions during auto turn
    this._updateActionButtons();

    setTimeout(() => {
      if (combatSystem.state !== 'autoTurn') return;

      const actions = combatSystem.runPartyAI(current);

      // Animate attacks
      for (const action of actions) {
        if (action.type === 'attack' && action.result?.hit && action.target) {
          this._animateAttack(action.target.id, action.target.alive);
          if (!action.target.alive) {
            this._animateDeath(action.target.id);
          }
        }
      }

      this._updateAllCombatantSprites();
      this._updateHighlights();
      this._updateTurnBar();
      this._updateInfoBar();

      // Check if combat ended
      if (combatSystem.state === 'victory' || combatSystem.state === 'defeat') {
        return;
      }

      // End auto-AI turn
      combatSystem.endTurn();
    }, ENEMY_AI_DELAY_MS);
  }

  // ── Victory / Defeat Overlay ────────────────────────

  _showVictoryOverlay(rewards) {
    this._showOverlay('victory', rewards);
  }

  _showDefeatOverlay() {
    this._showOverlay('defeat', null);
  }

  _showOverlay(type, rewards) {
    if (this._overlayEl) return;

    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    // Clear combat action focus
    focusManager.clear();

    this._overlayEl = document.createElement('div');
    this._overlayEl.className = 'combat-overlay';
    this._overlayEl.id = 'combat-overlay';
    this._overlayEl.setAttribute('role', 'dialog');
    this._overlayEl.setAttribute('aria-label', type === 'victory' ? 'Victory' : 'Defeat');

    const panel = document.createElement('div');
    panel.className = 'combat-overlay-panel';

    // Title
    const title = document.createElement('h2');
    title.className = `combat-overlay-title combat-overlay-title--${type}`;
    title.textContent = type === 'victory' ? 'VICTORY' : 'DEFEAT';
    panel.appendChild(title);

    if (type === 'victory' && rewards) {
      // Rewards
      const rewardsDiv = document.createElement('div');
      rewardsDiv.className = 'combat-overlay-rewards';

      const xpRow = document.createElement('div');
      xpRow.className = 'combat-overlay-reward-row';
      xpRow.innerHTML = `<span class="combat-overlay-reward-label">Experience</span><span class="combat-overlay-reward-value">+${rewards.xp} XP</span>`;
      rewardsDiv.appendChild(xpRow);

      const goldRow = document.createElement('div');
      goldRow.className = 'combat-overlay-reward-row';
      goldRow.innerHTML = `<span class="combat-overlay-reward-label">Gold</span><span class="combat-overlay-reward-value">+${rewards.gold}</span>`;
      rewardsDiv.appendChild(goldRow);

      if (rewards.items && rewards.items.length > 0) {
        const itemsDiv = document.createElement('div');
        itemsDiv.className = 'combat-overlay-items';
        for (let i = 0; i < rewards.items.length; i++) {
          const item = rewards.items[i];
          const roll = rewards.lootRolls?.[i];
          const itemEl = document.createElement('div');
          itemEl.className = `combat-overlay-item item-rarity-${item.rarity || 'normal'}`;
          let text = `Found: ${item.name}`;
          if (roll) {
            text += ` [Roll: ${roll.roll}]`;
          }
          itemEl.textContent = text;
          itemsDiv.appendChild(itemEl);
        }
        rewardsDiv.appendChild(itemsDiv);
      }

      panel.appendChild(rewardsDiv);
    } else if (type === 'defeat') {
      const msg = document.createElement('p');
      msg.className = 'combat-overlay-message';
      msg.textContent = 'Your party has been defeated. The caravan limps away to safety...';
      panel.appendChild(msg);
    }

    // Continue button
    const btn = document.createElement('button');
    btn.className = 'combat-overlay-btn';
    btn.id = 'combat-continue-btn';
    btn.setAttribute('data-focusable', 'true');
    btn.textContent = 'Continue';
    btn.addEventListener('click', () => {
      this._onCombatEnd(type, rewards);
    });
    panel.appendChild(btn);

    this._overlayEl.appendChild(panel);
    uiRoot.appendChild(this._overlayEl);

    // Push focus group for overlay
    focusManager.pushGroup('combat-overlay', panel);
  }

  _onCombatEnd(type, rewards) {
    // Apply rewards to party
    if (type === 'victory' && rewards && combatSystem._party) {
      const party = combatSystem._party;
      if (party.gold !== undefined) {
        party.gold += rewards.gold;
      }
      // XP distribution would go here in a future milestone
    }

    // Transition back
    sceneManager.transition(this._returnScene, this._returnData);
  }

  // ── Event Subscriptions ─────────────────────────────

  _subscribeEvents() {
    this._eventHandlers.onTurn = (data) => {
      this._updateTurnBar();
      this._updateInfoBar();
      this._updateHighlights();
      this._updateTileEffects();
      this._updateStatusIcons();
      this._updateActionButtons();

      // If enemy turn, schedule AI
      if (data.state === 'enemyTurn') {
        this._scheduleEnemyAI();
      }

      // If auto-AI turn, schedule party AI
      if (data.state === 'autoTurn') {
        this._scheduleAutoAI();
      }
    };

    this._eventHandlers.onLog = (data) => {
      this._addLogEntry(data.message);
    };

    this._eventHandlers.onVictory = (data) => {
      this._updateTurnBar();
      this._updateAllCombatantSprites();
      setTimeout(() => {
        this._showVictoryOverlay(data.rewards);
      }, 500);
    };

    this._eventHandlers.onDefeat = () => {
      this._updateTurnBar();
      this._updateAllCombatantSprites();
      setTimeout(() => {
        this._showDefeatOverlay();
      }, 500);
    };

    this._eventHandlers.onAttack = (data) => {
      this._updateAllCombatantSprites();
      this._updateTurnBar();
    };

    this._eventHandlers.onMove = (data) => {
      this._updateCombatantSprite(data.unit);
      this._updateHighlights();
    };

    this._eventHandlers.onAutoTurnStart = (data) => {
      this._scheduleAutoAI();
    };

    this._eventHandlers.onCompanionReady = (data) => {
      // After the owner's turn ends, run companion AI
      // The companion auto-acts right before the turn advances
      this._pendingCompanionAction = data;
    };

    eventBus.on('combat:turn', this._eventHandlers.onTurn);
    eventBus.on('combat:log', this._eventHandlers.onLog);
    eventBus.on('combat:victory', this._eventHandlers.onVictory);
    eventBus.on('combat:defeat', this._eventHandlers.onDefeat);
    eventBus.on('combat:attack', this._eventHandlers.onAttack);
    eventBus.on('combat:move', this._eventHandlers.onMove);
    eventBus.on('combat:autoTurnStart', this._eventHandlers.onAutoTurnStart);
    eventBus.on('combat:companionReady', this._eventHandlers.onCompanionReady);
  }

  _unsubscribeEvents() {
    if (this._eventHandlers.onTurn) {
      eventBus.off('combat:turn', this._eventHandlers.onTurn);
    }
    if (this._eventHandlers.onLog) {
      eventBus.off('combat:log', this._eventHandlers.onLog);
    }
    if (this._eventHandlers.onVictory) {
      eventBus.off('combat:victory', this._eventHandlers.onVictory);
    }
    if (this._eventHandlers.onDefeat) {
      eventBus.off('combat:defeat', this._eventHandlers.onDefeat);
    }
    if (this._eventHandlers.onAttack) {
      eventBus.off('combat:attack', this._eventHandlers.onAttack);
    }
    if (this._eventHandlers.onMove) {
      eventBus.off('combat:move', this._eventHandlers.onMove);
    }
    if (this._eventHandlers.onAutoTurnStart) {
      eventBus.off('combat:autoTurnStart', this._eventHandlers.onAutoTurnStart);
    }
    if (this._eventHandlers.onCompanionReady) {
      eventBus.off('combat:companionReady', this._eventHandlers.onCompanionReady);
    }
    this._eventHandlers = {};
  }

  // ── Skill Menu ──────────────────────────────────────

  _openSkillMenu() {
    const current = combatSystem.getCurrentTurn();
    if (!current || current.type !== 'player') return;

    const availableSkills = skillSystem.getAvailableSkills(current);
    if (availableSkills.length === 0) {
      this._addLogEntry('No skills available! Learn skills from the Skill Tree panel.');
      return;
    }

    this._closeSkillMenu();

    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this._skillMenuEl = document.createElement('div');
    this._skillMenuEl.className = 'combat-skill-menu';
    this._skillMenuEl.id = 'combat-skill-menu';

    const header = document.createElement('div');
    header.className = 'combat-skill-menu-header';
    header.textContent = 'Skills';
    this._skillMenuEl.appendChild(header);

    const list = document.createElement('div');
    list.className = 'combat-skill-menu-list';

    for (const { skill, tree, level } of availableSkills) {
      const check = skillSystem.canUseSkill(current, skill.id);
      const cooldown = skillSystem.getCooldown(current.id, skill.id);
      const manaCost = Array.isArray(skill.manaCost)
        ? skill.manaCost[Math.min(level - 1, skill.manaCost.length - 1)]
        : skill.manaCost;

      const btn = document.createElement('button');
      btn.className = 'combat-skill-btn';
      btn.id = `combat-skill-btn-${skill.id}`;
      btn.setAttribute('data-focusable', 'true');
      btn.setAttribute('data-skill-id', skill.id);

      if (!check.canUse) {
        btn.disabled = true;
        btn.classList.add('combat-skill-btn--disabled');
      }

      btn.innerHTML = `
        <span class="combat-skill-name">${skill.name}</span>
        <span class="combat-skill-costs">
          <span class="combat-skill-ap">${skill.apCost} AP</span>
          ${manaCost > 0 ? `<span class="combat-skill-mana">${manaCost} MP</span>` : ''}
          ${cooldown > 0 ? `<span class="combat-skill-cd">CD: ${cooldown}</span>` : ''}
        </span>
      `;

      btn.addEventListener('click', () => {
        if (!check.canUse) return;
        this._selectSkill(skill.id);
      });

      list.appendChild(btn);
    }

    // Cancel button
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'combat-skill-btn combat-skill-btn--cancel';
    cancelBtn.id = 'combat-skill-cancel';
    cancelBtn.setAttribute('data-focusable', 'true');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => this._closeSkillMenu());
    list.appendChild(cancelBtn);

    this._skillMenuEl.appendChild(list);
    uiRoot.appendChild(this._skillMenuEl);

    // Push focus group for skill menu
    focusManager.pushGroup('combat-skills', this._skillMenuEl);
  }

  _closeSkillMenu() {
    if (this._skillMenuEl && this._skillMenuEl.parentNode) {
      this._skillMenuEl.parentNode.removeChild(this._skillMenuEl);
      // Only pop if the current group is the skills group
      const topGroup = focusManager._currentGroup && focusManager._currentGroup();
      if (topGroup && topGroup.name === 'combat-skills') {
        focusManager.popGroup();
      }
    }
    this._skillMenuEl = null;
    this._selectedSkillId = null;
  }

  _selectSkill(skillId) {
    this._closeSkillMenu();

    const current = combatSystem.getCurrentTurn();
    if (!current) return;

    this._selectedSkillId = skillId;
    this._selectedAction = 'skill';

    // Get valid targets
    const targets = skillSystem.getSkillTargets(current, skillId);
    const found = skillSystem.findSkill(skillId);
    const skill = found ? found.skill : null;

    // For self-targeting skills, execute immediately
    if (skill && skill.targetType === 'self') {
      const result = skillSystem.useSkill(current, skillId, {
        x: current.gridX, y: current.gridY, combatant: current,
      });
      this._selectedAction = null;
      this._selectedSkillId = null;
      this._afterPlayerAction();
      return;
    }

    if (targets.length === 0) {
      this._addLogEntry('No valid targets for this skill!');
      this._selectedAction = null;
      this._selectedSkillId = null;
      return;
    }

    // If only one target, auto-select
    if (targets.length === 1 && targets[0].combatant) {
      const result = skillSystem.useSkill(current, skillId, targets[0]);
      if (result.success) {
        // Animate hits
        for (const r of result.results) {
          if (r.target && r.damage > 0) {
            this._animateAttack(r.target.id, r.target.alive);
            if (!r.target.alive) {
              this._animateDeath(r.target.id);
            }
          }
        }
      }
      this._selectedAction = null;
      this._selectedSkillId = null;
      this._afterPlayerAction();
      return;
    }

    // Show target selection for skills
    this._updateHighlights();
    this._showTargetPrompt('Select a target for ' + (skill ? skill.name : 'skill'));

    this._setupTileClickHandler((x, y) => {
      const occupant = combatSystem.getUnitAt(x, y);
      const selectedTarget = { x, y, combatant: occupant };

      // Validate target is in the valid targets list
      const isValid = targets.some(t => t.x === x && t.y === y);
      if (!isValid) return;

      this._hideTargetPrompt();
      this._removeTileClickHandler();

      const result = skillSystem.useSkill(current, this._selectedSkillId, selectedTarget);
      if (result.success) {
        for (const r of result.results) {
          if (r.target && r.damage > 0) {
            this._animateAttack(r.target.id, r.target.alive);
            if (!r.target.alive) {
              this._animateDeath(r.target.id);
            }
          }
        }
      }

      this._selectedAction = null;
      this._selectedSkillId = null;
      this._afterPlayerAction();
    }, targets);
  }

  // ── Tile Effect Rendering ──────────────────────────

  _updateTileEffects() {
    if (!this._tileEffectContainer) return;
    this._tileEffectContainer.removeChildren();

    const effects = tileEffectsSystem.getAllEffects();
    if (effects.length === 0) return;

    const gfx = new Graphics();
    const ts = this._tileSize;

    for (const effect of effects) {
      const px = this._gridOffsetX + effect.x * ts;
      const py = this._gridOffsetY + effect.y * ts;

      gfx.rect(px + 2, py + 2, ts - 4, ts - 4);
      gfx.fill({ color: effect.color, alpha: effect.alpha });

      // Small effect type indicator in corner
      const dotSize = 4;
      gfx.circle(px + ts - 8, py + 8, dotSize);
      gfx.fill({ color: effect.color, alpha: 0.8 });
    }

    this._tileEffectContainer.addChild(gfx);
  }

  // ── Status Effect Icons on Combatants ──────────────

  _updateStatusIcons() {
    // Update the turn bar entries with status effect indicators
    if (!this._turnBarEl) return;

    const entries = this._turnBarEl.querySelectorAll('.combat-turn-entry');
    entries.forEach(entry => {
      // Remove old status icons
      const oldIcons = entry.querySelector('.combat-status-icons');
      if (oldIcons) oldIcons.remove();
    });

    for (let i = 0; i < combatSystem.turnOrder.length; i++) {
      const c = combatSystem.turnOrder[i];
      if (!c.statusEffects || c.statusEffects.length === 0) continue;

      const entry = entries[i + 1]; // +1 because first child is the label
      if (!entry) continue;

      const iconsDiv = document.createElement('div');
      iconsDiv.className = 'combat-status-icons';

      for (const status of c.statusEffects) {
        const icon = document.createElement('span');
        icon.className = `combat-status-icon combat-status-icon--${status.id}`;
        icon.title = `${status.name} (${status.duration > 0 ? status.duration + ' turns' : 'permanent'})`;
        icon.textContent = this._getStatusIconChar(status.id);
        iconsDiv.appendChild(icon);
      }

      entry.appendChild(iconsDiv);
    }
  }

  _getStatusIconChar(statusId) {
    const icons = {
      burning: 'F',
      frozen: 'C',
      stunned: 'S',
      poisoned: 'P',
      blessed: 'B',
      cursed: 'X',
      rooted: 'R',
      hasted: 'H',
      shielded: 'D',
      invisible: 'I',
      taunted: 'T',
      slowed: 'W',
      regenerating: '+',
      exposed: 'E',
      defending: 'D',
    };
    return icons[statusId] || '?';
  }

  // ── Debug Commands ──────────────────────────────────

  _registerDebugCommands() {
    const dc = window.__debug?.console;
    if (!dc) return;

    dc.registerCommand('startcombat', (args) => {
      const preset = args[0] || '4x4';
      const match = preset.match(/^(\d+)x(\d+)$/);
      if (!match) return 'Usage: startcombat <NxM> (e.g., 4x4, 2x3)';

      // This command only makes sense if we are already in combat
      return `Combat is already active: ${combatSystem.players.length} vs ${combatSystem.enemies.length}`;
    }, 'Show current combat info');

    dc.registerCommand('killall', () => {
      for (const e of combatSystem.enemies) {
        if (e.alive) {
          e.hp = 0;
          e.alive = false;
          eventBus.emit('combat:kill', { attacker: null, target: e });
        }
      }
      combatSystem.checkCombatEnd();
      this._updateAllCombatantSprites();
      this._updateTurnBar();
      return 'All enemies killed.';
    }, 'Kill all enemies (debug)');

    dc.registerCommand('combatgrid', () => {
      return [
        `Grid: ${combatSystem.cols}×${combatSystem.rows}`,
        `TileSize: ${this._tileSize}px`,
        `Offset: (${this._gridOffsetX}, ${this._gridOffsetY})`,
        `Viewport: ${this._width}×${this._height}`,
        `GridRect: x=${this._gridOffsetX}–${this._gridOffsetX + combatSystem.cols * this._tileSize}, y=${this._gridOffsetY}–${this._gridOffsetY + combatSystem.rows * this._tileSize}`,
      ].join('\n');
    }, 'Show combat grid layout parameters');

    dc.registerCommand('tilecursor', () => {
      if (!this._tileCursorActive) return 'No tile selection active.';
      const valid = this._tileCursorValidTiles.map(t => `(${t.x},${t.y})`).join(' ');
      return [
        `Cursor: (${this._tileCursorX}, ${this._tileCursorY})  active:${this._tileCursorActive}`,
        `Valid tiles (${this._tileCursorValidTiles.length}): ${valid || 'all'}`,
        `Overlay: ${this._tileOverlayEl ? 'present' : 'absent'}`,
      ].join('\n');
    }, 'Show tile cursor state');

    dc.registerCommand('clicktest', (args) => {
      const x = parseInt(args[0]);
      const y = parseInt(args[1]);
      if (isNaN(x) || isNaN(y)) return 'Usage: clicktest <col> <row>';
      const px = this._gridOffsetX + x * this._tileSize + this._tileSize / 2;
      const py = this._gridOffsetY + y * this._tileSize + this._tileSize / 2;
      return `Tile (${x},${y}) center = pixel (${Math.round(px)}, ${Math.round(py)}). Click that pixel to test.`;
    }, 'Show pixel coords for a tile (for manual click testing)');

    // Expose on window.__debug.commands
    if (window.__debug) {
      window.__debug.commands.killall = () => {
        for (const e of combatSystem.enemies) {
          if (e.alive) {
            e.hp = 0;
            e.alive = false;
          }
        }
        combatSystem.checkCombatEnd();
        this._updateAllCombatantSprites();
        this._updateTurnBar();
      };
      window.__debug.commands.combatgrid = () => ({
        cols: combatSystem.cols,
        rows: combatSystem.rows,
        tileSize: this._tileSize,
        offsetX: this._gridOffsetX,
        offsetY: this._gridOffsetY,
        width: this._width,
        height: this._height,
      });
    }
  }
}
